import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Tappable } from '@vectord/components';
import { batch, useDispatch, useSelector } from 'react-redux';

import {
  editCrashCourseBatchSelector,
  learnerUpdateActionSelector,
  setBatchSequence,
  setLearnerEmails,
  setModuleRepeat,
  setEditCrashCourseBatchModalState,
  UPDATE_ACTIONS,
  validateEditCrashCourseBatchInput,
} from 'features/classCreation';
import ClassesVisibility from '../ClassesVisibility';
import DisclaimerDetails from '../DisclaimerDetails';
import Field from 'components/Field';
import FieldErrors from 'components/FieldErrors';
import Switch from 'components/Switch';
import styles from './AddOrRemoveLearnersField.module.scss';

export default function AddOrRemoveLearnersField() {
  const dispatch = useDispatch();
  const [inputErrors, setInputErrors] = useState(null);
  const [inputEmails, setInputEmails] = useState('');
  const [inputSequence, setInputSequence] = useState(null);
  const [inputModuleRepeat, setInputModuleRepeat] = useState(false);
  const editCrashCourseBatch = useSelector(editCrashCourseBatchSelector);
  const learnerUpdateAction = useSelector(learnerUpdateActionSelector);
  const isAdd = learnerUpdateAction === UPDATE_ACTIONS.add;
  const fieldLabel = isAdd ? 'Add Learners' : 'Remove Learners';

  const emails = useMemo(
    () =>
      inputEmails
        .split(',')
        .filter((email) => email.trim().length !== 0)
        .map((email) => email.trim()),
    [inputEmails]
  );

  const handleClick = useCallback(
    (event) => {
      setInputErrors(null);
      const errors = validateEditCrashCourseBatchInput(
        editCrashCourseBatch,
        emails
      );
      if (Object.keys(errors).length === 0) {
        batch(() => {
          dispatch(setLearnerEmails(emails));
          dispatch(setBatchSequence(inputSequence));
          dispatch(setModuleRepeat(inputModuleRepeat));
          dispatch(setEditCrashCourseBatchModalState(true));
        });
      } else {
        setInputErrors(errors);
      }
    },
    [dispatch, editCrashCourseBatch, emails, inputSequence, inputModuleRepeat]
  );

  const handleEmailsChange = useCallback((event) => {
    setInputEmails(event.target.value);
  }, []);

  const handleSequenceChange = useCallback((event) => {
    setInputSequence(event.target.value);
  }, []);

  const handleModuleRepeatChange = useCallback((event) => {
    setInputModuleRepeat(event.target.checked);
  }, []);

  if (learnerUpdateAction) {
    return (
      <div className={styles.editEmailSection}>
        <Field
          name="mentee_emails"
          label={fieldLabel}
          clientErrors={inputErrors}
          className="m-b-m"
        >
          <textarea
            className={classNames(styles.inputEmails, 'scroll')}
            value={inputEmails}
            onChange={handleEmailsChange}
            placeholder="Enter comma separated email ids"
          />
        </Field>
        {isAdd && (
          <>
            <Field
              name="sequence"
              label="Sequence"
              clientErrors={inputErrors}
              className="m-b-m"
            >
              <input
                type="number"
                value={inputSequence}
                className={styles.inputSequence}
                placeholder="Enter the trimester in which the batch should be visible to the mentee"
                onChange={handleSequenceChange}
              />
            </Field>
            <Field
              name="module_repeat"
              label="Module Repeat"
              clientErrors={inputErrors}
              hint="Mark 'Yes' if learner is attending for improvement in associated module"
              className={classNames(styles.inputModuleRepeat, 'm-b-m')}
            >
              <Switch
                checked={Boolean(inputModuleRepeat)}
                onChange={handleModuleRepeatChange}
                className="m-l-xs m-b-xxs"
                small
              />
              <span className="m-l-10 m-b-xxs">
                {Boolean(inputModuleRepeat) ? 'Yes' : 'No'}
              </span>
            </Field>
          </>
        )}
        <ClassesVisibility />
        <FieldErrors
          name="past_classes_status"
          errors={inputErrors}
        />
        <DisclaimerDetails />
        <Tappable
          className="btn btn-primary btn-long m-t-20"
          onClick={handleClick}
        >
          Confirm Changes
        </Tappable>
      </div>
    );
  } else return null;
}
